import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, UserMinus, ArrowRight, Search, Users } from "lucide-react";
import { errMsg } from "./apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const selectCls = "bg-[#0A0A0C] border border-[#27272A] px-3 py-2 text-xs focus:outline-none focus:border-[#C5A059]";

const SLOT_LABELS = {
  GK: "Gardien", DG: "Latéral gauche", DC: "Déf. central", DD: "Latéral droit",
  "6": "Milieu déf. (6)", "8": "Milieu (8)", AG: "Ailier gauche", AD: "Ailier droit", "9": "Attaquant (9)",
};

export default function AdminReservistes() {
  const [reservistes, setReservistes] = useState(null);
  const [teams, setTeams] = useState([]);
  const [recherche, setRecherche] = useState("");
  const [cibles, setCibles] = useState({});
  const [slots, setSlots] = useState({});

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/admin/reservistes`, { withCredentials: true }),
      axios.get(`${API}/admin/teams`, { params: { detection_id: "kinshasa-2026" }, withCredentials: true }),
    ]).then(([r, t]) => {
      setReservistes(r.data);
      setTeams(t.data);
    }).catch(() => { toast.error("Erreur de chargement"); setReservistes([]); });
  }, []);
  useEffect(load, [load]);

  const placer = async (r) => {
    const target = cibles[r.registration_id];
    const slot = slots[r.registration_id];
    if (!target) return;
    try {
      const { data } = await axios.post(`${API}/admin/reservistes/placer`, {
        registration_id: r.registration_id,
        slot: slot || undefined,
      }, { withCredentials: true });
      toast.success(data.message);
      setCibles((c) => ({ ...c, [r.registration_id]: "" }));
      setSlots((s) => ({ ...s, [r.registration_id]: "" }));
      load();
    } catch (e) {
      toast.error(errMsg(e, "Placement impossible"));
    }
  };

  const retirer = async (r) => {
    if (!window.confirm(`Retirer ${r.prenom} ${r.nom} de la liste des réservistes ? (son inscription est conservée)`)) return;
    try {
      await axios.delete(`${API}/admin/reservistes/${r.registration_id}`, { withCredentials: true });
      toast.success("Réserviste retiré de la liste");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Retrait impossible"));
    }
  };

  if (!reservistes) return <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-[#C5A059]" /></div>;

  const filtres = reservistes.filter((r) => {
    const q = recherche.trim().toLowerCase();
    if (!q) return true;
    return `${r.prenom} ${r.nom} ${r.email || ""} ${r.poste_principal || ""} ${r.poste_secondaire || ""}`.toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-reservistes" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-xl uppercase font-bold flex items-center gap-2">
            <Users size={18} className="text-[#C5A059]" /> Liste des Réservistes
          </h2>
          <p className="text-sm text-[#71717A] mt-1">
            Joueurs confirmés sans place dans les équipes (postes principal et secondaire pourvus partout).
          </p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <input
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Rechercher un réserviste…"
            data-testid="reservistes-recherche"
            className={`${selectCls} pl-9 w-64`}
          />
        </div>
      </div>

      <div className="text-xs text-[#71717A]" data-testid="reservistes-compteur">
        {filtres.length} réserviste(s)
      </div>

      <div className="border border-[#27272A] divide-y divide-[#27272A]">
        {filtres.length === 0 && (
          <p className="px-5 py-10 text-center text-[#71717A] text-sm">Aucun réserviste pour le moment.</p>
        )}
        {filtres.map((r) => {
          const genre = r.genre || "masculin";
          const ciblesPossibles = teams.filter((t) =>
            (t.genre || "masculin") === genre && t.statut === "en_formation"
          );
          const slotOptions = [];
          const p1 = r.poste_principal;
          const p2 = r.poste_secondaire;
          if (p1) slotOptions.push({ value: p1, label: `Principal : ${p1}` });
          if (p2) slotOptions.push({ value: p2, label: `Secondaire : ${p2}` });
          return (
            <div key={r.registration_id} data-testid={`reserviste-row-${r.registration_id.slice(0, 8)}`} className="px-5 py-3.5 flex flex-wrap items-center gap-3 text-sm">
              <div className="min-w-[190px]">
                <p className="font-medium">{r.prenom} {r.nom}</p>
                <p className="text-[11px] text-[#71717A]">{r.email}</p>
              </div>
              <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#27272A] text-[#A1A1AA]">
                {genre === "féminin" ? "Fille" : "Garçon"}
              </span>
              <span className="text-xs text-[#C5A059]" title={`Principal: ${r.poste_principal} | Secondaire: ${r.poste_secondaire || "—"}`}>
                {r.poste_principal}{r.poste_secondaire ? ` / ${r.poste_secondaire}` : ""}
              </span>
              <span className="flex-1" />
              {slotOptions.length > 1 && (
                <select
                  value={slots[r.registration_id] || ""}
                  onChange={(e) => setSlots({ ...slots, [r.registration_id]: e.target.value })}
                  data-testid={`reserviste-slot-${r.registration_id.slice(0, 8)}`}
                  className={selectCls}
                >
                  <option value="">Poste auto</option>
                  {slotOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              )}
              <select
                value={cibles[r.registration_id] || ""}
                onChange={(e) => setCibles({ ...cibles, [r.registration_id]: e.target.value })}
                data-testid={`reserviste-select-${r.registration_id.slice(0, 8)}`}
                className={selectCls}
              >
                <option value="" disabled>Équipe cible…</option>
                {ciblesPossibles.map((t) => (
                  <option key={t.id} value={t.id}>{t.nom} ({t.joueurs.length}/11)</option>
                ))}
              </select>
              <button
                onClick={() => placer(r)}
                disabled={!cibles[r.registration_id]}
                data-testid={`reserviste-placer-${r.registration_id.slice(0, 8)}`}
                title="Placer dans l'équipe"
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#C5A059]/60 text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ArrowRight size={11} /> Placer
              </button>
              <button
                onClick={() => retirer(r)}
                data-testid={`reserviste-retirer-${r.registration_id.slice(0, 8)}`}
                title="Retirer de la liste des réservistes"
                className="p-1.5 text-[#71717A] hover:text-red-400 transition-colors"
              >
                <UserMinus size={15} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
